/**
 * A little module that will find matches in an array, and keep track of where you are in that array. Allows you to call next, previous, etc. It's the power behind the up and down arrows next to the search box when you press ctrl+f.
 */

import { FormattedLogMessage } from "../../FormattedLogMessage";

// Just loops forever. Also handles next and prev very well.
const searchResultIterator = (array: number[] = []) => {
	let nextIndex = 0;
	let first = true;
	let lastMove: string | null = null;
	return {
		getActiveIndex: () => nextIndex + 1,
		values: array,
		length: array.length,
		setActiveIndex: (ind) => (nextIndex = ind),
		next() {
			// We decrement after every prev call. If the last call was "prev," and we're trying to go forward, we need to add 2 to the next index.
			if (lastMove === "prev") nextIndex = nextIndex + 2;
			lastMove = "next";
			if (first) first = false;
			if (nextIndex === array.length) {
				nextIndex = 0;
			} else if (nextIndex === -1) {
				nextIndex = array.length - 1;
			}
			return {
				searchResultNumber: nextIndex + 1,
				value: array[nextIndex++],
				done: false,
			};
		},
		previous() {
			// We increment after every next call. If the last call was "next," and we're trying to go back, we need to subtract 2 from the next index.
			if (lastMove === "next") nextIndex = nextIndex - 2;
			lastMove = "prev";
			if (first) {
				first = false;
				nextIndex = array.length - 1;
			}

			if (nextIndex === array.length) {
				nextIndex = 0;
			}

			if (nextIndex === -1) {
				nextIndex = array.length - 1;
			}

			return {
				searchResultNumber: nextIndex + 1,
				value: array[nextIndex--],
				done: false,
			};
		},
	};
};

export type SearchResultIterator = ReturnType<typeof searchResultIterator>;

export class CtrlFSearch {
	searchString: string = "";

	iterator: SearchResultIterator = searchResultIterator([]);

	performLogSearch(arr: FormattedLogMessage[], str: string) {
		this.searchString = str;
		// they've backspaced until there's no string.
		const matches: number[] = [];
		if (str !== "") {
			for (let i = 0; i < arr.length; i++) {
				const log = arr[i];
				if (log.allLogArgs.toLowerCase().includes(str.toLowerCase())) {
					matches.push(i);
				}
			}
		}
		this.iterator = searchResultIterator(matches);
	}

	performStringSearch(arr: string[], str: string) {
		this.searchString = str;
		// they've backspaced until there's no string.
		const matches: number[] = [];
		if (str !== "") {
			for (let i = 0; i < arr.length; i++) {
				const line = arr[i];
				if (line.toLowerCase().includes(str.toLowerCase())) {
					matches.push(i);
				}
			}
		}
		this.iterator = searchResultIterator(matches);
	}

	getIterator() {
		return this.iterator;
	}
}
